import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, Download, CheckCircle2, AlertTriangle, XCircle, Copy, ArrowLeft } from 'lucide-react';
import api, { BACKEND_URL } from '@/lib/api';

export default function BulkImportUsersDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // {total, ok, warnings, errors, rows}
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);   // {created, skipped, rows}

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    // simple GET; backend returns text/csv attachment
    const url = `${BACKEND_URL}/api/users/bulk-import/template`;
    window.open(url, '_blank');
  };

  const runPreview = async () => {
    if (!file) return toast.error('Choose a CSV file first');
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Don't set Content-Type explicitly — let axios add the multipart boundary automatically.
      const r = await api.post('/users/bulk-import/preview', fd);
      setPreview(r.data);
      setStep('preview');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to parse CSV';
      toast.error(typeof msg === 'string' ? msg : 'Failed to parse CSV');
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    if (!preview?.rows?.length) return;
    setExecuting(true);
    try {
      const r = await api.post('/users/bulk-import/execute', { rows: preview.rows });
      setResult(r.data);
      setStep('done');
      if ((r.data?.created || 0) > 0) toast.success(`Created ${r.data.created} user${r.data.created > 1 ? 's' : ''}`);
      if (onImported) onImported(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to import users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to import users');
    } finally {
      setExecuting(false);
    }
  };

  const copyGeneratedPasswords = () => {
    if (!result?.rows) return;
    const lines = ['Email,Password'];
    result.rows.forEach(r => {
      if (r.outcome === 'created' && r.generated_password) {
        lines.push(`${r.email},${r.generated_password}`);
      }
    });
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Passwords copied to clipboard');
  };

  const statusIcon = (s) => {
    if (s === 'ok') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    if (s === 'warning') return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Bulk Import Users</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns: email, name, role, department, position, manager_email, password.
            Leave the password column blank to auto-generate a strong one.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded bg-muted/30">
              <div>
                <div className="font-medium text-sm">Not sure of the format?</div>
                <div className="text-xs text-muted-foreground">Download a template CSV with the correct headers.</div>
              </div>
              <Button variant="outline" onClick={downloadTemplate} className="gap-1"><Download className="w-4 h-4" /> Template</Button>
            </div>
            <div className="border-2 border-dashed rounded p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <input
                id="bulk-csv"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <label htmlFor="bulk-csv" className="cursor-pointer text-sm text-[#002FA7] font-medium">
                {file ? `Selected: ${file.name}` : 'Choose a CSV file'}
              </label>
              <div className="text-xs text-muted-foreground mt-1">Max 2 MB</div>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <span>Total: <b>{preview.total}</b></span>
              <span className="text-green-700">✓ Ready: <b>{preview.ok}</b></span>
              {preview.warnings > 0 && <span className="text-yellow-700">⚠ Warnings: <b>{preview.warnings}</b></span>}
              {preview.errors > 0 && <span className="text-red-700">✗ Errors: <b>{preview.errors}</b></span>}
            </div>
            <div className="max-h-[400px] overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Row</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Role</th>
                    <th className="p-2 text-left">Department</th>
                    <th className="p-2 text-left">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className={`border-t ${r.status === 'error' ? 'bg-red-50' : r.status === 'warning' ? 'bg-yellow-50' : ''}`}>
                      <td className="p-2">{r.row}</td>
                      <td className="p-2">{statusIcon(r.status)}</td>
                      <td className="p-2 font-mono">{r.email}</td>
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">{r.role}</td>
                      <td className="p-2">{r.department}</td>
                      <td className="p-2 text-red-700">
                        {(r.errors || []).join('; ')}
                        {(r.warnings || []).length > 0 && (
                          <span className="text-yellow-700 block">{(r.warnings || []).join('; ')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-3">
            <div className="p-4 bg-green-50 border border-green-200 rounded text-sm">
              ✓ Import complete — <b>{result.created}</b> created, {result.skipped} skipped.
            </div>
            {result.rows?.some(r => r.generated_password) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">Auto-generated passwords — copy them now, they cannot be retrieved later.</div>
                  <Button variant="outline" size="sm" onClick={copyGeneratedPasswords} className="gap-1"><Copy className="w-3 h-3" /> Copy all</Button>
                </div>
                <div className="max-h-[300px] overflow-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Email</th>
                        <th className="p-2 text-left">Password</th>
                        <th className="p-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2 font-mono">{r.email}</td>
                          <td className="p-2 font-mono">{r.generated_password || <span className="text-muted-foreground">(user-provided)</span>}</td>
                          <td className="p-2">
                            {r.outcome === 'created'
                              ? <Badge className="text-[10px]">Created</Badge>
                              : <Badge variant="outline" className="text-[10px]">Skipped</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={runPreview} disabled={!file || previewing}>{previewing ? 'Analyzing…' : 'Analyze CSV'}</Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')} className="gap-1"><ArrowLeft className="w-4 h-4" /> Back</Button>
              <Button onClick={runExecute} disabled={executing || preview.ok + preview.warnings === 0}>
                {executing ? 'Creating…' : `Create ${preview.ok + preview.warnings} user${(preview.ok + preview.warnings) === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={close}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
